import { useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getPayBreakdownPath, getPayBreakdownSection } from "./routes";
import { PAY_BREAKDOWN_SECTION, type PayBreakdownSection } from "./types";

interface UsePayBreakdownSectionOptions {
  availability: Record<PayBreakdownSection, boolean>;
  isReady: boolean;
}

export const usePayBreakdownSection = ({
  availability,
  isReady,
}: UsePayBreakdownSectionOptions) => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { periodStart, section: routedSection } = useParams<{
    periodStart: string;
    section: string;
  }>();
  const requestedSection = getPayBreakdownSection(routedSection);
  const activeSection = availability[requestedSection]
    ? requestedSection
    : PAY_BREAKDOWN_SECTION.breakdown;

  useEffect(() => {
    if (!isReady || !periodStart) return;

    const normalizedPath = getPayBreakdownPath(periodStart, activeSection);
    if (normalizedPath === pathname) return;

    navigate({ pathname: normalizedPath, search }, { replace: true });
  }, [activeSection, isReady, navigate, pathname, periodStart, search]);

  const setActiveSection = (nextSection: PayBreakdownSection) => {
    if (!periodStart) return;

    navigate(
      { pathname: getPayBreakdownPath(periodStart, nextSection), search },
      { replace: false },
    );
  };

  return { activeSection, setActiveSection };
};
